import React from "react";
import { Link } from "react-router-dom";
import Window from "../components/Window";
import { IconTile } from "../components/Icon";

const sections: { title: string; body: React.ReactNode }[] = [
  {
    title: "The short version",
    body: (
      <>
        We keep as little as we can, we never sell anything, and there are no
        ads or trackers. Your letters are only readable by you and your pen
        pal. You can download everything or delete your account any time from
        My Profile.
      </>
    ),
  },
  {
    title: "What we store",
    body: (
      <ul className="ml-5 list-disc">
        <li>
          <strong>Your login:</strong> your email address and password, kept by
          Google's Firebase Authentication. Your password is never visible to
          us. Your email is never shown to other members.
        </li>
        <li>
          <strong>Your profile:</strong> your username and whatever you choose
          to fill in. You decide which details (name, age, location, pronouns)
          other members can see, and whether you're listed in the directory.
        </li>
        <li>
          <strong>Posts</strong> on the PenPal Board, which every signed-in
          member can read. Anonymous posts don't show your name to anyone.
        </li>
        <li>
          <strong>Community room posts and replies</strong>, which every
          signed-in member can read. Anonymous posts show "anonymous"; only our
          moderators can look up who wrote one, and only if it's reported.
        </li>
        <li>
          <strong>Private Circles:</strong> a circle's name, purpose, and
          guidelines are visible to signed-in members; its posts, comments, and
          member list are only seen by the circle's members.
        </li>
        <li>
          <strong>The PenPal Post:</strong> writing you send in is read by our
          editors first. Once delivered, it and its comments are visible to
          signed-in members. Returned or waiting pieces are only seen by you and
          the editors. Anonymous pieces show "anonymous" to readers.
        </li>
        <li>
          <strong>Letters, pen pal requests, and kudos:</strong> only the
          sender and the person they're for can read them. We (Slug and Worm)
          don't read your letters. The only time we look at one is when
          someone reports it to us.
        </li>
        <li>
          <strong>Little extras:</strong> your plant, pins, and block list.
          Pins and block lists are private to you.
        </li>
        <li>
          <strong>When you joined</strong> and when you agreed to the
          guidelines (that you're 18+).
        </li>
      </ul>
    ),
  },
  {
    title: "Reports",
    body: (
      <>
        When you report someone, the report (with a copy of the post or letter
        you reported) is saved for our moderators and emailed to them through{" "}
        <a href="https://web3forms.com" target="_blank" rel="noreferrer">
          Web3Forms
        </a>
        , a form-to-email service. Moderators can see who sent a report so they
        can follow up. The person you report is never told. Reports are kept
        even if you delete your account, for everyone's safety.
      </>
    ),
  },
  {
    title: "Who can see what",
    body: (
      <ul className="ml-5 list-disc">
        <li>Nothing on PenPal Board is visible to people who aren't signed in, except these public pages.</li>
        <li>Other members see your profile (only the details you chose) and your board posts.</li>
        <li>
          Our small volunteer moderation team can see stored data when they
          need to, for example to look into a report. We don't browse letters.
        </li>
        <li>We never sell or share your data with advertisers.</li>
      </ul>
    ),
  },
  {
    title: "Where it lives",
    body: (
      <>
        PenPal Board runs on Google Firebase (hosting, sign-in, and the
        database). Google processes the data to run the service. Your browser
        also remembers a few conveniences on your own device, like which
        windows you minimized and your display settings. Those never leave
        your device.
      </>
    ),
  },
  {
    title: "Your choices",
    body: (
      <ul className="ml-5 list-disc">
        <li>Change what's visible on your profile any time.</li>
        <li>Block anyone. They aren't told.</li>
        <li>
          <strong>Download my data</strong> and <strong>Delete my account</strong>{" "}
          are at the bottom of My Profile. Deleting removes your profile,
          posts, letters you wrote, requests, kudos, and pen pal connections.
          Letters other people wrote to you stay with them.
        </li>
      </ul>
    ),
  },
  {
    title: "Questions",
    body: (
      <>
        Email us at{" "}
        <a href="mailto:2slgbtqiapenpal@gmail.com">2slgbtqiapenpal@gmail.com</a>.
        See also our <Link to="/guidelines">community guidelines</Link>.
      </>
    ),
  },
];

export default function Privacy() {
  return (
    <Window title="privacy.txt" tone="tan" minimizable={false}>
      <h1 className="window-heading flex items-center gap-3">
        <IconTile name="lock" tone="blue" size={44} /> Privacy
      </h1>
      <p className="window-sub">Plain words about your data. Last updated October 2026.</p>
      <div className="flex flex-col gap-4">
        {sections.map((s) => (
          <section key={s.title} className="profile-box bg-white">
            <div className="profile-box-body">
              <h2 className="font-display text-lg font-semibold">{s.title}</h2>
              <div className="text-sm">{s.body}</div>
            </div>
          </section>
        ))}
      </div>
    </Window>
  );
}
